import { Fragment } from "react";
import { Box } from "@chakra-ui/react";
import {
  COST_CARD_EVENT_PATH,
  CostCardSurface,
  customEvent,
  Spacing,
  trackCostCardSlotCta,
} from "goi_common";

import { BaseButton } from "../../components";
import { baseColors } from "../../tokens";

/** 프로토타입 v23 일러스트 좌표계. 컨테이너 폭이 달라져도 그림이 늘어나지 않게 고정한다 */
const ILLUST_WIDTH = 260;
const ILLUST_HEIGHT = 156;
const BOX_SIZE = 66;
/** 두 상자 사이 */
const BOX_GAP = 8;
/** 싼 곳 상자 왼쪽 끝. 라벨 왼쪽 끝(16)부터 원 오른쪽 끝(244)까지가 폭 260 의 가운데 오게 맞춘 값 */
const BOX_LEFT = 28;
const BOX_RIGHT = BOX_LEFT + BOX_SIZE + BOX_GAP;
/** 두 상자의 밑변. 라벨(아래 22px) 위에 놓인다 */
const BOX_TOP = ILLUST_HEIGHT - 22 - BOX_SIZE;

/**
 * "가장 싼 곳 vs 가장 비싼 곳" 그림. 비싼 쪽 상자 위 오렌지가 "같은 지역인데 이만큼 차이"를 보여준다.
 * 애니메이션은 뺐다(2026-10-16 영민). 라벨을 14px 로 키우면서 상자 간격(8px)은 두고 상자를 키워 라벨이 겹치지 않게 했다.
 * 에셋 없이 CSS로 그린다 (PriceRiseIllustration 과 같은 방식).
 */
const RegionGapIllustration = () => (
  <Box
    position="relative"
    width={`${ILLUST_WIDTH}px`}
    height={`${ILLUST_HEIGHT}px`}
    maxWidth="100%"
    aria-hidden
  >
    <Box
      position="absolute"
      left={`${BOX_LEFT}px`}
      top={`${BOX_TOP}px`}
      width={`${BOX_SIZE}px`}
      height={`${BOX_SIZE}px`}
      bg={baseColors.white}
    />
    <Box
      position="absolute"
      left={`${BOX_RIGHT}px`}
      top={`${BOX_TOP}px`}
      width={`${BOX_SIZE}px`}
      height={`${BOX_SIZE}px`}
      bg={baseColors.white}
    />
    {/* 비싼 곳 증가분 */}
    <Box
      position="absolute"
      left={`${BOX_RIGHT}px`}
      bottom={`${ILLUST_HEIGHT - BOX_TOP}px`}
      width={`${BOX_SIZE}px`}
      height={`${BOX_SIZE}px`}
      bg={baseColors.orange_300}
      borderTopRadius="4px"
    />

    {/* 싼 곳 기준선 */}
    <Box
      position="absolute"
      left={`${BOX_LEFT - 10}px`}
      top={`${BOX_TOP - 2}px`}
      width={`${BOX_SIZE * 2 + BOX_GAP + 20}px`}
      borderTop={`2px dashed ${baseColors.label_alternative_2}`}
    />

    <Box
      position="absolute"
      left={`${BOX_RIGHT + BOX_SIZE + 12}px`}
      top={`${BOX_TOP - 32}px`}
      width="64px"
      height="64px"
      borderRadius="50%"
      bg={baseColors.label_alternative_2}
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      fontSize="14px"
      fontWeight={700}
      lineHeight="1.25"
      color={baseColors.white}
    >
      얼마나
      <br />
      차이?
    </Box>

    {[
      { left: BOX_LEFT, label: "가장 싼 곳" },
      { left: BOX_RIGHT, label: "가장 비싼 곳" },
    ].map(({ left, label }) => (
      <Box
        key={label}
        position="absolute"
        left={`${left + BOX_SIZE / 2 - 45}px`}
        bottom={0}
        width="90px"
        textAlign="center"
        whiteSpace="nowrap"
        fontSize="14px"
        lineHeight="20px"
        color={baseColors.label_alternative_1}
      >
        {label}
      </Box>
    ))}
  </Box>
);

interface LocalCostCardProps {
  /** 줄바꿈은 \n. 글 주제에 맞춰 호출부가 고른다 (예: "무빈소 장례 비용,\n우리 지역은 얼마일까요?") */
  title: string;
  /** 카드가 붙은 지면. 이벤트 두 개(공통, 지면별)를 이 값으로 고른다 */
  surface: CostCardSurface;
  /** 이벤트는 이 컴포넌트가 쏜다. 호출부는 이동만 한다 */
  onStart: () => void;
}

/**
 * "우리 지역 장례 비용" 카드. 가이드북·후기·호스피스·요양·장지에 붙는다(실험 #361~#363, costCardExperiment.ts).
 * www 장례 비용 계산기(/match/cost-calculator/)로 보낸다. 화면은 프로토타입 v23 기준 (2026-10-15 영민).
 * 클릭 이벤트는 공통(click-cost_calc-entry_card, text = 지면)과 지면별(click-{path}-costcard-calc) 두 개를 쏜다.
 */
export const LocalCostCard = ({
  title,
  surface,
  onStart,
}: LocalCostCardProps) => {
  const lines = title.split("\n");

  return (
    // BaseStack 의 alignItems 는 DOM 으로 새어 콘솔 경고가 난다(wiki/backlog.md known bug). PrimeSection 과 같이 Box 로 둔다.
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      bg={baseColors.yellow_150}
      borderRadius="12px"
      p="28px 20px 20px"
    >
      <Box
        className="headline_1_sb"
        color={baseColors.label_normal}
        textAlign="center"
        wordBreak="keep-all"
      >
        {lines.map((line, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </Box>

      <Spacing gap={16} />
      <RegionGapIllustration />
      <Spacing gap={10} />

      <Box
        className="body_1_normal_r"
        color={baseColors.label_alternative_1}
        textAlign="center"
        wordBreak="keep-all"
      >
        장례식장부터 장지까지
        <br />
        1분이면 알 수 있어요
      </Box>

      <Spacing gap={16} />

      <Box width="100%" maxWidth="400px">
        <BaseButton
          type="assistive_1"
          size="large"
          onClick={() => {
            customEvent({
              newGtm: {
                path: "cost_calc",
                trigger_and_target: "entry_card",
                text: surface,
              },
            });
            customEvent({
              newGtm: {
                path: COST_CARD_EVENT_PATH[surface],
                trigger_and_target: "costcard-calc",
              },
            });
            trackCostCardSlotCta(surface, "B");
            onStart();
          }}
        >
          <Box
            as="span"
            className="body_1_normal_sb"
            color={baseColors.label_alternative_1}
          >
            우리 지역 장례 비용 알아보기
          </Box>
        </BaseButton>
      </Box>
    </Box>
  );
};
